import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, User, Settings, Lock, Shield, RefreshCw, Crown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLock } from '../../contexts/LockContext';
import { useAdmin } from '../../contexts/AdminContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import NotificationDropdown from './NotificationDropdown';
import UpdateModal from '../Common/UpdateModal';
import PlusModal from '../Common/PlusModal';
import PlusStatusModal from '../Common/PlusStatusModal';
import usePlusStatus from '../../hooks/usePlusStatus';
import UserAvatar from '../Common/UserAvatar';

const HeaderUserControls = ({ onSectionChange }) => {
  const { t } = useTranslation('shared');
  const navigate = useNavigate();
  const { user, isPlusUser, logout } = useAuth();
  const { lock } = useLock();
  const { isAdmin } = useAdmin();
  const { unreadCount, addNotification, notifications } = useNotification();
  const { setActiveTab } = useSettings();
  const { isServerOnline } = useConnectionMonitor();

  const plusStatus = usePlusStatus();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [showPlusStatusModal, setShowPlusStatusModal] = useState(false);
  const profileRef = useRef(null);

  const [updateState, setUpdateState] = useState({
    status: 'idle',
    info: null,
    error: null,
    errorDetails: null,
    currentVersion: null,
    lastCheckedAt: null,
    progress: null,
  });
  const updateNotifAddedRef = useRef(false);

  // Electron güncelleme listener'ları
  useEffect(() => {
    if (!window.api) return;

    let isMounted = true;

    const hydrateState = async () => {
      try {
        const bootstrapState = await window.api.getUpdateState?.();
        if (!isMounted || !bootstrapState) return;
        setUpdateState((prev) => ({ ...prev, ...bootstrapState, progress: bootstrapState.progress ?? null }));
        if (['available', 'downloading', 'downloaded', 'installing'].includes(bootstrapState.status)) {
          setShowUpdateModal(true);
        }
        if (!bootstrapState.currentVersion) {
          const version = await window.api.getAppVersion?.();
          if (isMounted && version) {
            setUpdateState((prev) => ({ ...prev, currentVersion: version }));
          }
        }
      } catch (error) {
        console.error('Güncelleme durumu alınamadı:', error);
      }
    };

    hydrateState();

    const unsubscribers = [
      window.api.onUpdateChecking?.(() => {
        setUpdateState((prev) => ({ ...prev, status: 'checking', error: null }));
      }),
      window.api.onUpdateAvailable?.((payload) => {
        setUpdateState((prev) => ({ ...prev, status: 'available', info: payload || null, error: null, errorDetails: null, progress: null }));
        setShowUpdateModal(true);
        // Güncelleme bildirimini dropdown'a ekle (oturum başına bir kez)
        if (!updateNotifAddedRef.current) {
          updateNotifAddedRef.current = true;
          addNotification({
            type: 'update',
            title: t('headerUserControls.updateAvailableTitle'),
            message: t('headerUserControls.updateAvailableMessage', { version: payload?.version ? `v${payload.version}` : t('headerUserControls.newVersionFallback') }),
          });
        }
      }),
      window.api.onUpdateDownloadProgress?.((payload) => {
        setUpdateState((prev) => ({ ...prev, status: 'downloading', progress: payload || prev.progress }));
        setShowUpdateModal(true);
      }),
      window.api.onUpdateDownloaded?.((payload) => {
        setUpdateState((prev) => ({ ...prev, status: 'downloaded', info: payload || prev.info, error: null, errorDetails: null, progress: { percent: 100 } }));
        setShowUpdateModal(true);
      }),
      window.api.onUpdateInstalling?.((payload) => {
        setUpdateState((prev) => ({ ...prev, status: 'installing', info: payload?.info || prev.info, error: null, errorDetails: null, progress: { percent: 100 } }));
        setShowUpdateModal(true);
      }),
      window.api.onUpdateNotAvailable?.(() => {
        setUpdateState((prev) => ({ ...prev, status: 'idle', info: null, progress: null, error: null, errorDetails: null }));
      }),
      window.api.onUpdateError?.((payload) => {
        const errorMessage = typeof payload === 'string' ? payload : payload?.message || payload?.friendlyMessage || t('headerUserControls.unknownError');
        const details = typeof payload === 'object' && payload !== null ? payload.details || payload : null;
        setUpdateState((prev) => ({ ...prev, status: 'error', error: errorMessage, errorDetails: details, progress: null }));
      }),
    ].filter(Boolean);

    return () => {
      isMounted = false;
      unsubscribers.forEach((fn) => typeof fn === 'function' && fn());
      window.api?.removeUpdateListeners?.();
    };
  }, []);

  const handleCheckUpdates = async () => {
    if (!window.api?.checkForUpdates) return;
    try {
      setUpdateState((prev) => ({ ...prev, status: 'checking', error: null, errorDetails: null }));
      await window.api.checkForUpdates();
    } catch (error) {
      setUpdateState((prev) => ({ ...prev, status: 'error', error: error?.message || String(error), errorDetails: error?.details || null }));
    }
  };

  const handleDownloadUpdate = async () => {
    if (!window.api?.downloadUpdate) return;
    try {
      setUpdateState((prev) => ({ ...prev, status: 'downloading', error: null, errorDetails: null }));
      const result = await window.api.downloadUpdate();
      if (result && result.ok === false && result.error) {
        const errorMessage = typeof result.error === 'string' ? result.error : result.error?.message || t('headerUserControls.unknownError');
        setUpdateState((prev) => ({ ...prev, status: 'error', error: errorMessage, errorDetails: typeof result.details === 'object' ? result.details : result.error?.details || null, progress: null }));
      }
    } catch (error) {
      setUpdateState((prev) => ({ ...prev, status: 'error', error: error?.message || String(error), errorDetails: error?.details || null, progress: null }));
    }
  };

  const showUpdateBadge = updateState.info && updateState.status !== 'error';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const handleProfileAction = (action) => {
    setShowProfileMenu(false);
    if (action === 'profile') navigate('/profile');
    else if (action === 'lock') lock();
    else if (action === 'admin') navigate('/admin');
    else if (action === 'updates') setShowUpdateModal(true);
    else if (action === 'settings') {
      setActiveTab('user');
      navigate('/settings');
      onSectionChange?.('settings');
    }
    else if (action === 'logout') logout();
  };

  if (!user) return null;

  const avatarUrl = user?.avatarUrl || user?.avatar_url;
  const displayName = user?.name || user?.email || t('headerUserControls.defaultDisplayName');

  // Sadece Electron'da güncelleme merkezini göster
  const isElectron = Boolean(window.api);

  return (
    <div className="flex items-center gap-2">
      {/* Bildirim Butonu */}
      <div className="relative" id="notification-container">
        <button
          id="notification-button"
          onClick={() => {
            setShowNotifications((v) => !v);
            setShowProfileMenu(false);
          }}
          className="relative p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          title={t('headerUserControls.notifications')}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        <NotificationDropdown
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          positionClass="top-full right-0 mt-2"
        />
      </div>

      {/* Plus / Crown Butonu */}
      <button
        onClick={() => {
          setShowProfileMenu(false);
          setShowNotifications(false);
          if (plusStatus.isPlus) {
            setShowPlusStatusModal(true);
          } else {
            setShowPlusModal(true);
          }
        }}
        className="relative p-2 transition-colors"
        title={plusStatus.isPlus ? t('headerUserControls.plusMembership') : t('headerUserControls.upgradeToPlus')}
      >
        <Crown
          className="w-5 h-5"
          style={{ color: plusStatus.isPlus ? '#f59e0b' : '#4d6a8a' }}
        />
        {plusStatus.isTrial && plusStatus.remainingDays <= 3 && (
          <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-amber-400 rounded-full" />
        )}
      </button>

      {/* Profil Butonu */}
      <div className="relative" ref={profileRef}>
        <button
          onClick={() => {
            setShowProfileMenu((v) => !v);
            setShowNotifications(false);
          }}
          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title={t('headerUserControls.profile')}
        >
          <UserAvatar
            userId={user?.id?.toString()}
            avatarUrl={avatarUrl}
            name={displayName}
            size={28}
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 max-w-[100px] truncate hidden sm:block">
            {displayName}
          </span>
        </button>

        <AnimatePresence>
          {showProfileMenu && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full right-0 mt-2 w-52 bg-[#0d1424] dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-[10001]"
            >
              <div className="py-1">
                <button
                  onClick={() => handleProfileAction('profile')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-left"
                >
                  <User className="w-4 h-4" />
                  {t('headerUserControls.profile')}
                </button>
                <button
                  onClick={() => handleProfileAction('settings')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-left"
                >
                  <Settings className="w-4 h-4" />
                  {t('headerUserControls.settings')}
                </button>
                <button
                  onClick={() => handleProfileAction('lock')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-left"
                >
                  <Lock className="w-4 h-4" />
                  {t('headerUserControls.appLock')}
                </button>
                <button
                  onClick={() => handleProfileAction('logout')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10 text-left"
                >
                  <LogOut className="w-4 h-4" />
                  {t('headerUserControls.logout')}
                </button>
                {isElectron && (
                  <button
                    onClick={() => handleProfileAction('updates')}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-left"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="flex-1">{t('headerUserControls.updateCenter')}</span>
                    {showUpdateBadge && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    )}
                  </button>
                )}
                <div className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 border-t border-slate-100 dark:border-slate-700/60 select-none">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isServerOnline ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
                  {t('headerUserControls.serverLabel')} {isServerOnline ? t('headerUserControls.connected') : t('headerUserControls.disconnected')}
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleProfileAction('admin')}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-left"
                  >
                    <Shield className="w-4 h-4" />
                    {t('headerUserControls.admin')}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Güncelleme Merkezi Modal */}
      <UpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        state={updateState}
        onCheck={handleCheckUpdates}
        onDownload={handleDownloadUpdate}
      />

      {/* Plus Tanıtım Modalı (Free kullanıcılar) */}
      <PlusModal
        isOpen={showPlusModal}
        onClose={() => setShowPlusModal(false)}
      />

      {/* Plus Durum Modalı (Plus kullanıcılar) */}
      <PlusStatusModal
        isOpen={showPlusStatusModal}
        onClose={() => setShowPlusStatusModal(false)}
        statusData={plusStatus}
      />
    </div>
  );
};

export default HeaderUserControls;
